import moment from 'moment';

/**
 * Fixed-offset IST (Asia/Kolkata, UTC+5:30) helpers for the panchang stack.
 *
 * The application is Kerala-only: every panchang decision (star/thithi day
 * classification, two-star carry cutoffs, row date keys, wall-clock brackets)
 * and the booking-window policy must be evaluated on the IST wall clock no
 * matter where the browser runs. IST has no DST and a stable +5:30 offset, so
 * pure epoch arithmetic suffices — no timezone database.
 *
 * Convention: all Dates in the stack are TRUE instants (epoch-correct, the
 * same convention as panchang.json's ISO-UTC star/tithi start/end). These
 * helpers are the ONLY sanctioned way to turn such an instant into an IST
 * calendar day, wall-clock field, or display string. Never use local
 * getDate()/getHours()/moment(x).format(...) for panchang day/time decisions —
 * those read the browser's timezone. `window.currentdate` stays the source of
 * "which instant is now" (server-synced); these helpers are the decomposition
 * layer on top of it.
 */

export const IST_OFFSET_MS = 330 * 60_000;

const DAY_MS = 86_400_000;

export interface IstParts {
    year: number;
    /** 0-based month, like Date.getMonth() */
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    millisecond: number;
    /** 0=Sunday..6=Saturday, like Date.getDay() */
    weekday: number;
}

/** Accepts a Date, epoch ms, or ISO string (panchang.json stores ISO-UTC strings). */
export type IstInstant = Date | number | string;

function toEpoch(instant: IstInstant): number {
    if (instant instanceof Date) {
        return instant.getTime();
    }
    return typeof instant === 'string' ? Date.parse(instant) : Number(instant);
}

/** IST wall-clock fields of an instant (host-timezone independent). */
export function istParts(instant: IstInstant): IstParts {
    const shifted = new Date(toEpoch(instant) + IST_OFFSET_MS);
    return {
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth(),
        day: shifted.getUTCDate(),
        hour: shifted.getUTCHours(),
        minute: shifted.getUTCMinutes(),
        second: shifted.getUTCSeconds(),
        millisecond: shifted.getUTCMilliseconds(),
        weekday: shifted.getUTCDay(),
    };
}

/** The instant (epoch ms) at the given IST wall-clock time. Month is 0-based. */
export function istEpoch(year: number, month: number, day: number,
    hour = 0, minute = 0, second = 0, millisecond = 0): number {
    return Date.UTC(year, month, day, hour, minute, second, millisecond) - IST_OFFSET_MS;
}

/** The instant of IST midnight of the instant's IST calendar day. */
export function istStartOfDay(instant: IstInstant): number {
    const epoch = toEpoch(instant);
    return Math.floor((epoch + IST_OFFSET_MS) / DAY_MS) * DAY_MS - IST_OFFSET_MS;
}

/** Comparable IST calendar-day key, e.g. 2026-11-03 -> 20261103. */
export function istDayKey(instant: IstInstant): number {
    const p = istParts(instant);
    return p.year * 10_000 + (p.month + 1) * 100 + p.day;
}

/** n IST calendar days after the instant (same wall-clock time; no DST in IST). */
export function istAddDays(instant: IstInstant, days: number): number {
    return toEpoch(instant) + days * DAY_MS;
}

/** Whole IST calendar days from `from`'s IST day to `to`'s IST day. */
export function istDiffDays(to: IstInstant, from: IstInstant): number {
    return Math.round((istStartOfDay(to) - istStartOfDay(from)) / DAY_MS);
}

/** Format an instant's IST wall-clock with a moment format string. */
export function istFormat(instant: IstInstant, format: string): string {
    return moment.utc(toEpoch(instant) + IST_OFFSET_MS).format(format);
}

/** The panchang bracket time, e.g. "03:46 am". */
export function istFormatTime(instant: IstInstant): string {
    return istFormat(instant, 'hh:mm a');
}
